/**
 * `useNavigateToNote` — the single-entry "go to this note in the editor"
 * helper that EVERY cross-surface note navigation should route through.
 *
 * Why this exists (2026-05-18):
 *
 * The orange-pulse / scroll-to-row / auto-expand-folder behaviour the user
 * sees when arriving at a note from elsewhere is driven by a chain:
 *
 *   1. Caller writes `{ noteId, quotedText }` to `useHighlightTargetStore`.
 *   2. Caller calls `navigate(toEditor(noteId))`.
 *   3. URL changes → `NotebooksShellPane` picks up the new `noteId` param.
 *   4. `NotebooksTreePane` reads `activeNoteId`, walks ancestors, expands,
 *      scrolls, pulses.
 *   5. `EditorSurface` reads `highlightTarget`, finds quoted text in the
 *      preview pane, scrolls to centre, wraps in `<mark>` for 5s.
 *
 * Steps 3–5 all KEY OFF the URL change. If the caller forgets step 1 OR
 * step 2 — or if they put step 1 AFTER step 2 (a real footgun: navigate is
 * asynchronous, the editor mounts before the store write commits, and the
 * effect that reads the store sees an empty target) — the whole experience
 * silently dies.
 *
 * Three callsites have been observed making one or both of these mistakes:
 *
 *   - `<ResultCard>` card-body click goes to a side DRAWER, never navigates
 *     (URL never changes; nothing in the chain fires)
 *   - `<QuickSwitcher>` Cmd+P pick navigates but doesn't write the target
 *     (no highlight, but the navigation works)
 *   - `<CitedText>` chat citation pill — wired correctly, but in-place — if
 *     a refactor touches the pill, the order is easy to break
 *
 * This hook makes it impossible to forget. Callers call ONE function with
 * the noteId (and optionally a quoted span) and get the correct behaviour
 * every time.
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toEditor } from '@/routes';
import { useHighlightTargetStore } from '@/stores';
import { asNoteId } from '@/types';
import type { NoteId } from '@/types';

export interface NavigateToNoteOptions {
  /** The note to open. Accepts the branded `NoteId` or a plain string (we
   *  re-brand internally — most callers receive plain strings from search
   *  results / chat citations / quick-switcher candidates). */
  noteId: NoteId | string;
  /** Optional quoted text to scroll-and-pulse in the preview pane. Pass
   *  empty string (or omit) when no quote is available — the editor's
   *  pickup logic skips the find-and-pulse step but still gets the
   *  title-pulse + folder-expand + row-scroll. */
  quotedText?: string;
}

/**
 * Return a stable callback that primes the highlight store and navigates
 * to the editor for the given note. Always uses these two in that order
 * (store write first, then navigate) — the order matters; see hook docstring.
 *
 * Stable across renders unless the underlying `navigate` / `setTarget`
 * change (rare). Safe to depend on from a `useCallback` chain.
 *
 * Example:
 *
 * ```tsx
 * const goToNote = useNavigateToNote();
 * ...
 * <button onClick={() => goToNote({ noteId, quotedText: snippet })}>
 *   Open
 * </button>
 * ```
 */
export function useNavigateToNote(): (opts: NavigateToNoteOptions) => void {
  const navigate = useNavigate();
  const setHighlightTarget = useHighlightTargetStore((s) => s.setTarget);
  return useCallback(
    ({ noteId, quotedText = '' }) => {
      const branded = asNoteId(String(noteId));
      // Order matters: write the store FIRST, then navigate. The Editor
      // surface's mount effect reads the store synchronously on first
      // render; if we navigated first, that read could land before the
      // store write commits (zustand is sync but React batching could
      // re-order observable updates in a future scheduler).
      setHighlightTarget({ noteId: branded, quotedText });
      navigate(toEditor(branded));
    },
    [navigate, setHighlightTarget],
  );
}
